import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, MessageCircle, X, User } from 'lucide-react';
import { Link } from 'react-router-dom';

// قاعدة الأسئلة والأجوبة — كل عنصر ليه كلمات مفتاحية بيدور عليها في رسالة المستخدم
const FAQ_DATABASE = [
  {
    keywords: ['شحن', 'توصيل', 'يوصل', 'وصول', 'مدة'],
    answer: 'التوصيل داخل المدن الرئيسية بياخد من 2 إلى 4 أيام عمل، وللمناطق الأخرى من 4 إلى 7 أيام عمل.',
  },
  {
    keywords: ['ارجاع', 'إرجاع', 'استرجاع', 'استبدال'],
    answer: 'تقدر ترجعي أي منتج خلال 14 يوم من الاستلام طالما لسه بحالته الأصلية. التفاصيل كاملة في صفحة سياسة الإرجاع.',
  },
  {
    keywords: ['دفع', 'فيزا', 'ماستركارد', 'استلام', 'كاش'],
    answer: 'متاح الدفع الإلكتروني بالبطاقات، وكمان الدفع عند الاستلام في معظم المناطق.',
  },
  {
    keywords: ['طلب', 'اوردر', 'أوردر', 'تتبع', 'حالة الطلب'],
    answer: 'تقدري تتابعي طلبك بالتفصيل من صفحة "طلباتي" في حسابك، هتلاقي حالته لحظة بلحظة.',
  },
  {
    keywords: ['باسورد', 'كلمة المرور', 'كلمة مرور', 'نسيت'],
    answer: 'من صفحة تسجيل الدخول دوسي على "نسيت كلمة المرور" وهنبعتلك رابط إعادة التعيين على بريدك.',
  },
  {
    keywords: ['خصم', 'عرض', 'عروض', 'كود خصم', 'كوبون'],
    answer: 'تقدري تلاقي أحدث العروض والخصومات في قسم "عروض وخصومات" على الصفحة الرئيسية.',
  },
  {
    keywords: ['الغاء', 'إلغاء', 'الغي طلب'],
    answer: 'ممكن تلغي طلبك طالما لسه قيد التجهيز، من صفحة "طلباتي"، أو بالتواصل معانا في أقرب وقت.',
  },
  {
    keywords: ['فرع', 'محل', 'مكان', 'عنوان'],
    answer: 'إحنا متجر إلكتروني بالكامل من غير فروع، وبنوصل طلبك لحد باب البيت في كل أنحاء المملكة.',
  },
];

const SUGGESTED_QUESTIONS = [
  'إمتى هيوصلي الطلب؟',
  'إزاي أرجع منتج؟',
  'وسائل الدفع المتاحة إيه؟',
];

const findAnswer = (userText) => {
  const normalized = userText.toLowerCase().trim();
  const match = FAQ_DATABASE.find((item) =>
    item.keywords.some((keyword) => normalized.includes(keyword))
  );
  return match ? match.answer : null;
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages, isOpen, isTyping]);

  const respondTo = (userText) => {
    setIsTyping(true);

    // تأخير بسيط عشان يحس إنه رد حقيقي مش فوري بشكل مفاجئ
    setTimeout(() => {
      const answer = findAnswer(userText);

      const botMessage = {
        role: 'bot',
        content:
          answer ||
          'معنديش إجابة جاهزة على السؤال ده دلوقتي، بس فريق خدمة العملاء هيقدر يساعدك أكتر.',
        showContactLink: !answer,
      };

      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);
    }, 600);
  };

  const handleSend = (text) => {
    const messageText = text ?? input;
    if (!messageText.trim()) return;

    setMessages((prev) => [...prev, { role: 'user', content: messageText }]);
    setInput('');
    respondTo(messageText);
  };

  return (
    <div className="fixed bottom-6 left-6 z-50" dir="rtl">
      {/* نافذة المحادثة */}
      {isOpen && (
        <div className="mb-4 flex h-[28rem] w-80 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-100 sm:w-96">
          {/* رأس النافذة */}
          <div className="flex items-center justify-between bg-[#1B1B1F] px-4 py-3.5 text-white">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F2B705] text-[#1B1B1F]">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold leading-tight">مساعد MEME STORE</h3>
                <p className="flex items-center gap-1 text-xs text-white/70">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F2B705]" />
                  متصل الآن
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="rounded-full p-1.5 transition-colors duration-200 hover:bg-white/15"
              aria-label="إغلاق المحادثة"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* الرسائل */}
          <div className="flex-1 space-y-4 overflow-y-auto bg-[#F6F5F2] p-4 text-right">
            {messages.length === 0 && (
              <div>
                <div className="mt-4 text-center text-sm leading-relaxed text-gray-500">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#F2B705]/15">
                    <Bot className="h-6 w-6 text-[#1B1B1F]" />
                  </div>
                  أهلاً بيكِ في MEME STORE! 👋
                  <br />
                  اسأليني عن الشحن، الإرجاع، الدفع، أو أي حاجة تانية.
                </div>

                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => handleSend(q)}
                      className="rounded-full border border-[#F2B705] bg-white px-3 py-1.5 text-xs font-bold text-[#1B1B1F] transition-colors hover:bg-[#F2B705]/20"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'bot' && (
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#1B1B1F] text-[#F2B705]">
                    <Bot className="h-3.5 w-3.5" />
                  </div>
                )}

                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                    msg.role === 'user'
                      ? 'rounded-bl-md bg-[#F2B705] text-[#1B1B1F]'
                      : 'rounded-br-md bg-white text-gray-800 ring-1 ring-gray-100'
                  }`}
                >
                  {msg.content}

                  {msg.showContactLink && (
                    <Link
                      to="/contact-us"
                      onClick={() => setIsOpen(false)}
                      className="mt-2 block text-xs font-bold text-[#1B1B1F] underline decoration-[#F2B705] decoration-2 underline-offset-2"
                    >
                      تواصلي مع خدمة العملاء
                    </Link>
                  )}
                </div>

                {msg.role === 'user' && (
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#1B1B1F] text-white">
                    <User className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="flex items-end justify-start gap-2">
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#1B1B1F] text-[#F2B705]">
                  <Bot className="h-3.5 w-3.5" />
                </div>
                <div className="flex items-center gap-1 rounded-2xl rounded-br-md bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* حقل الإدخال */}
          <div className="border-t border-gray-100 bg-white p-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isTyping && handleSend()}
                placeholder="اكتبي سؤالك..."
                className="flex-1 rounded-full border-2 border-gray-200 px-4 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-[#F2B705]"
                disabled={isTyping}
              />
              <button
                onClick={() => handleSend()}
                className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 ${
                  isTyping
                    ? 'cursor-not-allowed bg-gray-300 text-white'
                    : 'bg-[#F2B705] text-[#1B1B1F] hover:bg-[#1B1B1F] hover:text-white active:scale-95'
                }`}
                disabled={isTyping}
                aria-label="إرسال"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* أيقونة تشغيل المحادثة العائمة */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="group relative flex h-16 w-16 items-center justify-center rounded-full bg-[#F2B705] text-[#1B1B1F] shadow-lg shadow-[#F2B705]/40 transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95"
        aria-label={isOpen ? 'إغلاق المحادثة' : 'افتح مساعد MEME STORE'}
      >
        {!isOpen && (
          <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#F2B705]/60" />
        )}
        {isOpen ? <X className="h-7 w-7" /> : <MessageCircle className="h-7 w-7" />}
      </button>
    </div>
  );
};

export default ChatBot;
